import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Toaster, toast } from 'sonner';
import {
  ArrowLeft,
  Building2,
  MapPin,
  Mail,
  Phone,
  Globe,
  Edit2,
  Trash2,
  Send,
  Plus,
  Clock,
  User,
  StickyNote,
  Search
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sizeLabels = {
  small: 'Piccola',
  medium: 'Media',
  large: 'Grande',
  enterprise: 'Enterprise'
};

const statusLabels = {
  new: 'Nuovo',
  contacted: 'Contattato',
  qualified: 'Qualificato',
  converted: 'Convertito',
  lost: 'Perso'
};

const LeadDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [emailData, setEmailData] = useState({ subject: '', body: '' });
  const [sendingEmail, setSendingEmail] = useState(false);

  const fetchLead = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/leads/${id}`);
      setLead(response.data);
      setEditData(response.data);
    } catch (error) {
      console.error('Error fetching lead:', error);
      toast.error('Lead non trovato');
      navigate('/dashboard');
    }
  }, [id, navigate]);

  const fetchNotes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/leads/${id}/notes`);
      setNotes(response.data);
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  }, [id]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchLead(), fetchNotes()]);
      setLoading(false);
    };
    loadData();
  }, [fetchLead, fetchNotes]);

  const handleUpdateLead = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(`${API_URL}/leads/${id}`, editData);
      setLead(response.data);
      setShowEditModal(false);
      toast.success('Lead aggiornato con successo!');
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleDeleteLead = async () => {
    try {
      await axios.delete(`${API_URL}/leads/${id}`);
      toast.success('Lead eliminato');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    try {
      await axios.post(`${API_URL}/leads/${id}/notes`, { content: newNote });
      setNewNote('');
      fetchNotes();
      toast.success('Nota aggiunta!');
    } catch (error) {
      toast.error('Errore nell\'aggiunta della nota');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${API_URL}/leads/${id}/notes/${noteId}`);
      fetchNotes();
      toast.success('Nota eliminata');
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!emailData.subject || !emailData.body) {
      toast.error('Compila tutti i campi');
      return;
    }

    setSendingEmail(true);
    try {
      const response = await axios.post(`${API_URL}/leads/${id}/send-email`, emailData);
      toast.success(response.data.message);
      setShowEmailModal(false);
      setEmailData({ subject: '', body: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const response = await axios.put(`${API_URL}/leads/${id}`, { status: newStatus });
      setLead(response.data);
      toast.success('Stato aggiornato!');
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!lead) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/dashboard')}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center">
                <Search className="w-5 h-5" />
              </div>
              <span className="font-bold text-xl text-slate-900 tracking-tight">Customer Scout</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="border-slate-200" data-testid="lead-detail-card">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center text-2xl font-bold text-slate-600">
                        {lead.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">{lead.name}</h1>
                        {lead.position && (
                          <p className="text-slate-500">{lead.position}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <Badge className={`status-${lead.status}`}>
                            {statusLabels[lead.status]}
                          </Badge>
                          <Badge className={`size-${lead.size}`}>
                            {sizeLabels[lead.size]}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" data-testid="edit-lead-btn">
                            <Edit2 className="w-4 h-4 mr-2" />
                            Modifica
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                          <DialogHeader>
                            <DialogTitle>Modifica Lead</DialogTitle>
                          </DialogHeader>
                          <form onSubmit={handleUpdateLead} className="space-y-4">
                            <div className="space-y-2">
                              <Label>Nome</Label>
                              <Input
                                value={editData.name || ''}
                                onChange={(e) => setEditData({...editData, name: e.target.value})}
                                data-testid="edit-name"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Email</Label>
                              <Input
                                type="email"
                                value={editData.email || ''}
                                onChange={(e) => setEditData({...editData, email: e.target.value})}
                                data-testid="edit-email"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Telefono</Label>
                              <Input
                                value={editData.phone || ''}
                                onChange={(e) => setEditData({...editData, phone: e.target.value})}
                                data-testid="edit-phone"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Azienda</Label>
                              <Input
                                value={editData.company || ''}
                                onChange={(e) => setEditData({...editData, company: e.target.value})}
                                data-testid="edit-company"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Posizione</Label>
                              <Input
                                value={editData.location || ''}
                                onChange={(e) => setEditData({...editData, location: e.target.value})}
                                data-testid="edit-location"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Dimensione</Label>
                              <Select 
                                value={editData.size} 
                                onValueChange={(v) => setEditData({...editData, size: v})}
                              >
                                <SelectTrigger data-testid="edit-size">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="small">Piccola</SelectItem>
                                  <SelectItem value="medium">Media</SelectItem>
                                  <SelectItem value="large">Grande</SelectItem>
                                  <SelectItem value="enterprise">Enterprise</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <Button type="submit" className="w-full" data-testid="save-edit-btn">
                              Salva Modifiche
                            </Button>
                          </form>
                        </DialogContent>
                      </Dialog>

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" size="sm" className="text-rose-600 hover:text-rose-700" data-testid="delete-lead-btn">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Elimina Lead</AlertDialogTitle>
                            <AlertDialogDescription>
                              Sei sicuro di voler eliminare questo lead? L'azione non può essere annullata.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annulla</AlertDialogCancel>
                            <AlertDialogAction onClick={handleDeleteLead} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-btn">
                              Elimina
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {lead.company && (
                      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <Building2 className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-xs text-slate-500">Azienda</p>
                          <p className="font-medium text-slate-900">{lead.company}</p>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <MapPin className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Posizione</p>
                        <p className="font-medium text-slate-900">{lead.location}</p>
                      </div>
                    </div>
                    {lead.email && (
                      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <Mail className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-xs text-slate-500">Email</p>
                          <p className="font-medium text-slate-900">{lead.email}</p>
                        </div>
                      </div>
                    )}
                    {lead.phone && (
                      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <Phone className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-xs text-slate-500">Telefono</p>
                          <p className="font-medium text-slate-900">{lead.phone}</p>
                        </div>
                      </div>
                    )}
                    {lead.website && (
                      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <Globe className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-xs text-slate-500">Sito Web</p>
                          <a 
                            href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-blue-600 hover:text-blue-700"
                          >
                            {lead.website}
                          </a>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <Clock className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Aggiunto il</p>
                        <p className="font-medium text-slate-900">
                          {new Date(lead.created_at).toLocaleDateString('it-IT')}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Notes Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="border-slate-200" data-testid="notes-section">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <StickyNote className="w-5 h-5" />
                    Note
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <form onSubmit={handleAddNote} className="mb-6">
                    <Textarea
                      placeholder="Aggiungi una nota..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      className="mb-3 min-h-[100px]"
                      data-testid="note-input"
                    />
                    <Button type="submit" size="sm" data-testid="add-note-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Aggiungi Nota
                    </Button>
                  </form>

                  {notes.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <StickyNote className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>Nessuna nota ancora</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {notes.map((note) => (
                        <div 
                          key={note.id} 
                          className="p-4 bg-slate-50 rounded-lg group"
                          data-testid={`note-${note.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <p className="text-slate-700 whitespace-pre-wrap">{note.content}</p>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-rose-600"
                              onClick={() => handleDeleteNote(note.id)}
                              data-testid={`delete-note-${note.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <p className="text-xs text-slate-400 mt-2">
                            {new Date(note.created_at).toLocaleString('it-IT')}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status Change */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
            >
              <Card className="border-slate-200">
                <CardHeader className="border-b border-slate-100 pb-4">
                  <CardTitle className="text-lg">Stato Lead</CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <Select value={lead.status} onValueChange={handleStatusChange}>
                    <SelectTrigger data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">Nuovo</SelectItem>
                      <SelectItem value="contacted">Contattato</SelectItem>
                      <SelectItem value="qualified">Qualificato</SelectItem>
                      <SelectItem value="converted">Convertito</SelectItem>
                      <SelectItem value="lost">Perso</SelectItem>
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>
            </motion.div>

            {/* Send Email */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="border-slate-200">
                <CardHeader className="border-b border-slate-100 pb-4">
                  <CardTitle className="text-lg">Contatta</CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
                    <DialogTrigger asChild>
                      <Button 
                        className="w-full" 
                        disabled={!lead.email}
                        data-testid="send-email-btn"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Invia Email
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Invia Email a {lead.name}</DialogTitle>
                        <DialogDescription>
                          L'email verrà inviata a {lead.email}
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleSendEmail} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Oggetto</Label>
                          <Input
                            value={emailData.subject}
                            onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                            placeholder="Oggetto dell'email"
                            data-testid="email-subject"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Messaggio</Label>
                          <Textarea
                            value={emailData.body}
                            onChange={(e) => setEmailData({...emailData, body: e.target.value})}
                            placeholder="Scrivi il tuo messaggio..."
                            className="min-h-[150px]"
                            data-testid="email-body"
                          />
                        </div>
                        <Button 
                          type="submit" 
                          className="w-full" 
                          disabled={sendingEmail}
                          data-testid="send-email-submit"
                        >
                          {sendingEmail ? 'Invio in corso...' : 'Invia Email'}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                  {!lead.email && (
                    <p className="text-xs text-slate-500 mt-2 text-center">
                      Nessuna email disponibile per questo lead
                    </p>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Source Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <Card className="border-slate-200">
                <CardHeader className="border-b border-slate-100 pb-4">
                  <CardTitle className="text-lg">Origine</CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                      {lead.source === 'web_search' ? (
                        <Globe className="w-5 h-5 text-slate-600" />
                      ) : (
                        <User className="w-5 h-5 text-slate-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {lead.source === 'web_search' ? 'Ricerca Web' : 'Inserimento Manuale'}
                      </p>
                      <p className="text-xs text-slate-500">
                        {new Date(lead.created_at).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LeadDetailPage;
